"use client";

import { useCallback } from "react";
import { useMutation } from "convex/react";
import posthog from "posthog-js";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { SupportKind } from "@convex/schemas/validators/enums";
import type {
  FeedbackType,
  Priority,
} from "@/components/support/feedback-form-fields";
import { supportDiagnosticsFields } from "@/lib/feedback/client-diagnostics";

interface SubmitSupportRequestInput {
  contactEmail: string;
  issueType: SupportKind | FeedbackType;
  message: string;
  pagePath?: string;
  relatedPages?: Array<string>;
  sourcePath?: string;
  priority?: Priority;
  submittedFrom: "help_launcher" | "in_app_feedback";
  attachmentStorageIds?: Array<Id<"_storage">>;
  screenRecordingId?: Id<"_storage">;
  submissionKey: string;
  honeypot?: string;
}

export function useSupportRequestSubmission() {
  const createRequest = useMutation(api.support.mutations.createRequest);

  return useCallback(
    async (input: SubmitSupportRequestInput) => {
      const result = await createRequest({
        contactEmail: input.contactEmail,
        issueType: input.issueType,
        message: input.message,
        pagePath: input.pagePath,
        relatedPages: input.relatedPages,
        sourcePath: input.sourcePath,
        priority: input.priority,
        metadata: { submitted_from: input.submittedFrom },
        attachmentStorageIds: input.attachmentStorageIds,
        screenRecordingId: input.screenRecordingId,
        ...supportDiagnosticsFields(),
        submissionKey: input.submissionKey,
        honeypot: input.honeypot,
      });

      if (input.submittedFrom === "in_app_feedback") {
        try {
          posthog.capture("feedback.submitted", {
            feedback_type: input.issueType,
            priority: input.priority,
            ticket_number: result.ticketNumber,
            has_screenshots: Boolean(input.attachmentStorageIds?.length),
            related_page: input.pagePath,
            route: input.sourcePath,
          });
        } catch {
          return result;
        }
      }

      return result;
    },
    [createRequest],
  );
}
